import { ViewStyle } from "react-native";
import { StyledButton } from "./styled";

const sizeStyles = {
  large: { label: "my-4 mx-8", roundness: 3 },
  small: { label: "my-2", roundness: 2.5 },
};

type Mode = "outlined" | "text" | "contained" | "secondary" | "light";

export interface Props extends Omit<React.ComponentProps<typeof StyledButton>, "mode"> {
  size?: keyof typeof sizeStyles;
  mode?: Mode;
  iconRight?: boolean;
  isValid?: boolean;
  style?: ViewStyle;
}

export default function Button({
  size,
  disabled,
  mode,
  iconRight,
  className,
  labelStyle,
  isValid = true,
  style,
  ...props
}: Props) {
  const sizeStyle = sizeStyles[size || "large"];
  const modeStyle = getModeStyle(mode, disabled, isValid);

  return (
    <StyledButton
      // reset the border color because React Native Paper's default value is conflicting with NativeWind on Android. When we use border-color (example border-red) with NativeWind, it sets each border independently (borderLeftColor, borderRightColor, etc.)
      style={[{ borderColor: undefined }, style]}
      {...props}
      disabled={disabled}
      mode={modeStyle.mode}
      theme={{ roundness: sizeStyle.roundness }}
      contentStyle={iconRight && "flex-row-reverse"}
      className={`
        ${modeStyle.button}
        ${className}
      `}
      labelStyle={`
        text-regular-tight
        font-500
        ${modeStyle.label}
        ${sizeStyle.label}
        ${labelStyle}
      `}
    />
  );
}

type ModeStyle = {
  mode: "contained" | "text" | "outlined";
  label: string;
  button: string;
};

export function getModeStyle(mode: Mode = "text", disabled: boolean = false, isValid: boolean = false): ModeStyle {
  let style;
  const key = [mode, isValid ? "valid" : "invalid", disabled ? "disabled" : "enabled"].join(",");

  switch (key) {
    case "contained,invalid,disabled":
    case "contained,invalid,enabled":
    case "contained,valid,disabled":
      style = { button: "bg-sky-light", label: "text-sky-dark" };
      break;
    case "contained,valid,enabled":
      style = { button: "bg-primary", label: "text-sky-white" };
      break;
    case "outlined,invalid,enabled":
    case "outlined,valid,enabled":
      style = { button: "border-primary" };
      break;
    case "outlined,invalid,disabled":
    case "outlined,valid,disabled":
      style = { button: "border-sky" };
      break;
    case "secondary,valid,enabled":
      style = { button: "bg-primary-lightest active:opacity-80", label: "text-primary" };
      break;
    case "light,valid,enabled":
      style = { button: "bg-sky-lighter active:opacity-80", label: "text-primary" };
      break;
  }

  return { mode: mode === "secondary" || mode === "light" ? "contained" : mode, button: "", label: "", ...style };
}
